import { useMemo } from 'react';

import { filterLeadHistoryItems } from '@app/components/LeadCard/ui/MessageHistory/lib';

import { useAddActions } from './useAddActions';
import { useAddComment } from './useAddComment';
import { useAddFile } from './useAddFile';
import { useAddRecord } from './useAddRecord';
import { useAddTask } from './useAddTask';
import { useBaseState, type UseBaseStateParams } from './useBaseState';
import { useCopyMessageInfo } from './useCopyMessageInfo';
import { useFooter } from './useFooter';
import { useNavigation } from './useNavigation';
import { useOrganizationComment } from './useOrganizationComment';
import { useTags } from './useTags';
import { useUserDetailsState } from './useUserDetailsState';

export const useProviderValue = (params: UseBaseStateParams) => {
  const baseState = useBaseState(params);

  const navigation = useNavigation({
    selectedLeadId: baseState.selectedLeadId,
  });

  const userDetailsState = useUserDetailsState({
    resetKey: baseState.selectedLeadId,
    selectedLeadUserDetails: baseState.selectedLeadUserDetails,
  });

  const tags = useTags({
    leadCardData: baseState.leadCardData,
    refetchLeadCard: baseState.refetchLeadCard,
    refreshLatestHistory: baseState.refreshLatestHistory,
    selectedBranchId: baseState.selectedBranchId,
    selectedLeadId: baseState.selectedLeadId,
    selectedLeadUserDetails: baseState.selectedLeadUserDetails,
  });

  const comment = useAddComment({
    leadCardData: baseState.leadCardData,
    refetchLeadCard: baseState.refetchLeadCard,
    refreshLatestHistory: baseState.refreshLatestHistory,
    selectedBranchId: baseState.selectedBranchId,
    selectedLeadId: baseState.selectedLeadId,
  });

  const file = useAddFile({
    leadCardData: baseState.leadCardData,
    refetchLeadCard: baseState.refetchLeadCard,
    refreshLatestHistory: baseState.refreshLatestHistory,
    selectedBranchId: baseState.selectedBranchId,
    selectedLeadId: baseState.selectedLeadId,
  });

  const record = useAddRecord({
    branches: baseState.branches,
    isBranchesLoading: baseState.isBranchesLoading,
    leadCardData: baseState.leadCardData,
    selectedBranchId: baseState.selectedBranchId,
    selectedLeadId: baseState.selectedLeadId,
  });

  const task = useAddTask({
    leadCardData: baseState.leadCardData,
    refetchLeadCard: baseState.refetchLeadCard,
    selectedBranchId: baseState.selectedBranchId,
    selectedLeadId: baseState.selectedLeadId,
  });

  const organizationComment = useOrganizationComment({
    leadCardData: baseState.leadCardData,
    setOrganizationComment: baseState.setOrganizationComment,
    organizationComment: baseState.organizationComment,
    refreshLatestHistory: baseState.refreshLatestHistory,
  });

  const footer = useFooter({
    applyLeadUpdate: baseState.applyLeadUpdate,
    leadCardData: baseState.leadCardData,
    refetchLeadCard: baseState.refetchLeadCard,
    refreshLatestHistory: baseState.refreshLatestHistory,
  });

  const addActions = useAddActions({
    openTagsModal: tags.openTagsModal,
    openCommentModal: comment.openCommentModal,
    openFileModal: file.openFileModal,
    openBranchSelectionModal: record.openBranchSelectionModal,
    openTaskModal: task.openTaskModal,
    handleOrganizationCommentClick:
      organizationComment.handleOrganizationCommentClick,
    isFileAvailable: file.isFileAvailable,
  });

  const copyMessageInfo = useCopyMessageInfo();

  const filteredLeadHistoryItems = useMemo(
    () =>
      filterLeadHistoryItems(baseState.selectedLeadHistoryItems, {
        sourceItems: userDetailsState.sourceItems,
        actionItems: userDetailsState.actionItems,
      }),
    [
      baseState.selectedLeadHistoryItems,
      userDetailsState.sourceItems,
      userDetailsState.actionItems,
    ]
  );

  return useMemo(
    () => ({
      ...baseState,
      ...navigation,
      ...userDetailsState,
      ...tags,
      ...comment,
      ...file,
      ...record,
      ...task,
      ...footer,
      ...addActions,
      ...organizationComment,
      ...copyMessageInfo,
      filteredLeadHistoryItems,
    }),
    [
      addActions,
      baseState,
      comment,
      copyMessageInfo,
      file,
      filteredLeadHistoryItems,
      footer,
      navigation,
      record,
      tags,
      task,
      userDetailsState,
      organizationComment,
    ]
  );
};

export type LeadCardContextValue = ReturnType<typeof useProviderValue>;
